import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Dropdown,
  Grid,
  InputGroup,
  InputGroupPrepend,
  InputGroupText,
  PageLoader,
  Row,
  Table,
  TextInput
} from '@met/react-components';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { toastr } from 'react-redux-toastr';
import AccessCodeModal from '../../components/AccessCodeModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import UpdateTerminalModal from '../../components/UpdateTerminalModal';
import AddAdyenTerminalModal from './AddAdyenTerminalModal';
import { adminActions, locationActions } from '../../store';

const providerOptions = [
  { label: 'Square', value: 'Square' },
  { label: 'Adyen', value: 'Adyen' }
];

const currencyOptions = [
  { label: 'Not set', value: '' },
  { label: 'USD', value: 'USD' },
  { label: 'CAD', value: 'CAD' }
];

const AdyenLocationDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { locationId } = useParams();
  const { data: location, isLoading, isError } = useSelector(state => state.admin.adminLocation);
  const isSaving = useSelector(state => state.admin.updateAdminLocationSettings.isLoading);
  const isUpdatingTerminal = useSelector(state => state.locations.updateDevice.isLoading);
  const [form, setForm] = useState({
    provider: 'Square',
    adyenMerchantAccount: '',
    adyenPosMerchantAccount: '',
    adyenStoreId: '',
    adyenCurrencyCode: ''
  });
  const [isAddTerminalOpen, setIsAddTerminalOpen] = useState(false);
  const [createdAccessCode, setCreatedAccessCode] = useState('');
  const [createdTerminalLocationId, setCreatedTerminalLocationId] = useState('');
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [terminalToDelete, setTerminalToDelete] = useState(null);

  const loadLocation = () => dispatch(adminActions.getAdminLocation(locationId));

  useEffect(() => {
    dispatch(adminActions.getAdminLocation(locationId)).catch(() => {
      toastr.error('Error: Location', 'Could not load this location.');
    });
  }, [dispatch, locationId]);

  useEffect(() => {
    if (location) {
      setForm({
        provider: location.provider || 'Square',
        adyenMerchantAccount: location.adyenMerchantAccount || '',
        adyenPosMerchantAccount: location.adyenPosMerchantAccount || '',
        adyenStoreId: location.adyenStoreId || '',
        adyenCurrencyCode: location.adyenCurrencyCode || ''
      });
    }
  }, [location]);

  const updateForm = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const saveSettings = () => {
    if (form.provider === 'Adyen' && !form.adyenMerchantAccount.trim()) {
      toastr.error('Validation error', 'An Adyen merchant account is required for Adyen locations.');
      return;
    }

    dispatch(adminActions.updateAdminLocationSettings(locationId, form))
      .then(() => toastr.success('Saved', 'Location settings updated.'))
      .catch(() => toastr.error('Error: Save settings', 'Could not update location settings.'));
  };

  const addTerminal = ({ locationId: selectedLocationId, accessCode }) => {
    setIsAddTerminalOpen(false);
    if (accessCode) {
      setCreatedAccessCode(accessCode);
      setCreatedTerminalLocationId(selectedLocationId);
      return;
    }

    finishAddingTerminal(selectedLocationId);
  };

  const finishAddingTerminal = selectedLocationId => {
    if (selectedLocationId === locationId) {
      loadLocation();
    } else {
      history.push(`/Admin/AdyenSettings/${encodeURIComponent(selectedLocationId)}`);
    }
  };

  const closeAccessCode = () => {
    setCreatedAccessCode('');
    if (createdTerminalLocationId) {
      finishAddingTerminal(createdTerminalLocationId);
      setCreatedTerminalLocationId('');
    }
  };

  const updateTerminal = terminal => {
    dispatch(locationActions.updateDevice(terminal))
      .then(() => {
        setSelectedTerminal(null);
        return loadLocation();
      })
      .then(() => toastr.success('Updated', 'Terminal updated.'))
      .catch(() => toastr.error('Error: Update terminal', 'Could not update terminal.'));
  };

  const deleteTerminal = () => {
    dispatch(locationActions.deleteDevice({ id: terminalToDelete }))
      .then(() => loadLocation())
      .then(() => toastr.success('Deleted', 'Terminal deleted.'))
      .catch(() => toastr.error('Error: Delete terminal', 'Could not delete terminal.'))
      .finally(() => setTerminalToDelete(null));
  };

  const terminalColumns = {
    terminalName: { label: 'Terminal' },
    terminalId: { label: 'Serial #' },
    deviceCodeId: { label: 'Square Device Code' },
    deviceId: { label: 'Square Device ID' },
    actions: {
      label: '',
      content: terminal => (
        <>
          <Button icon='edit' title='Edit terminal' onClick={() => setSelectedTerminal(terminal)} />
          <Button icon='trash' title='Delete terminal' onClick={() => setTerminalToDelete(terminal.id)} />
        </>
      )
    }
  };

  if (isLoading || (!isError && (!location || location.locationId !== locationId))) {
    return <Grid><Row><Col><PageLoader /></Col></Row></Grid>;
  }

  if (isError || !location) {
    return (
      <Grid fluid>
        <Row><Col><p role='alert'>Could not load location {locationId}.</p></Col></Row>
        <Row><Col><Button secondary onClick={() => history.push('/Admin/AdyenSettings')}>Back to locations</Button></Col></Row>
      </Grid>
    );
  }

  const terminals = (location.terminals || []).map(terminal => ({
    ...terminal,
    locationId: location.locationId
  }));

  return (
    <Grid fluid>
      <Row>
        <Col>
          <Card>
            <CardHeader>Location {location.locationId}</CardHeader>
            <CardBody>
              <Row>
                <Col md='6'>
                  <InputGroup>
                    <InputGroupPrepend><InputGroupText>Provider</InputGroupText></InputGroupPrepend>
                    <Dropdown
                      options={providerOptions}
                      getOptionValue={option => option.value}
                      getOptionLabel={option => option.label}
                      value={form.provider}
                      onChange={option => updateForm('provider', option?.value || 'Square')}
                    />
                  </InputGroup>
                </Col>
                <Col md='6'>
                  <InputGroup>
                    <InputGroupPrepend><InputGroupText>Test cards</InputGroupText></InputGroupPrepend>
                    <TextInput value={location.allowTestCards ? 'Enabled' : 'Disabled'} disabled />
                  </InputGroup>
                </Col>
              </Row>
              <Row>
                <Col md='6'>
                  <InputGroup>
                    <InputGroupPrepend><InputGroupText>Adyen merchant account</InputGroupText></InputGroupPrepend>
                    <TextInput value={form.adyenMerchantAccount} onChange={event => updateForm('adyenMerchantAccount', event.target.value)} />
                  </InputGroup>
                </Col>
                <Col md='6'>
                  <InputGroup>
                    <InputGroupPrepend><InputGroupText>POS merchant account</InputGroupText></InputGroupPrepend>
                    <TextInput value={form.adyenPosMerchantAccount} onChange={event => updateForm('adyenPosMerchantAccount', event.target.value)} />
                  </InputGroup>
                </Col>
              </Row>
              <Row>
                <Col md='6'>
                  <InputGroup>
                    <InputGroupPrepend><InputGroupText>Adyen store ID</InputGroupText></InputGroupPrepend>
                    <TextInput value={form.adyenStoreId} onChange={event => updateForm('adyenStoreId', event.target.value)} />
                  </InputGroup>
                </Col>
                <Col md='6'>
                  <InputGroup>
                    <InputGroupPrepend><InputGroupText>Adyen currency</InputGroupText></InputGroupPrepend>
                    <Dropdown
                      options={currencyOptions}
                      getOptionValue={option => option.value}
                      getOptionLabel={option => option.label}
                      value={form.adyenCurrencyCode}
                      onChange={option => updateForm('adyenCurrencyCode', option?.value || '')}
                    />
                  </InputGroup>
                </Col>
              </Row>
              <Row>
                <Col md='6'>
                  <InputGroup>
                    <InputGroupPrepend><InputGroupText>Square location ID</InputGroupText></InputGroupPrepend>
                    <TextInput value={location.squareLocationId || ''} disabled />
                  </InputGroup>
                </Col>
                <Col md='6'>
                  <InputGroup>
                    <InputGroupPrepend><InputGroupText>Square application ID</InputGroupText></InputGroupPrepend>
                    <TextInput value={location.squareApplicationId || ''} disabled />
                  </InputGroup>
                </Col>
              </Row>
              <Row>
                <Col md='6'>
                  <InputGroup>
                    <InputGroupPrepend><InputGroupText>Square credential references</InputGroupText></InputGroupPrepend>
                    <TextInput
                      value={[location.squareAccessKeyName, location.terminalSquareAccessKeyName].filter(Boolean).join(', ')}
                      disabled
                    />
                  </InputGroup>
                </Col>
              </Row>
            </CardBody>
            <CardFooter>
              <Button primary loading={isSaving} onClick={saveSettings}>Save settings</Button>
              <Button secondary onClick={() => history.push('/Admin/AdyenSettings')}>Back</Button>
            </CardFooter>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <CardHeader>
              Terminals
              <Button icon='plus' title='Add terminal' onClick={() => setIsAddTerminalOpen(true)} style={{ marginLeft: 'auto' }} />
            </CardHeader>
            {terminals.length === 0
              ? <CardBody>No terminals configured.</CardBody>
              : <Table columns={terminalColumns} data={terminals} />}
          </Card>
        </Col>
      </Row>
      <UpdateTerminalModal
        isLoading={isUpdatingTerminal}
        isOpen={Boolean(selectedTerminal)}
        onClose={() => setSelectedTerminal(null)}
        terminal={selectedTerminal}
        onTerminalUpdate={updateTerminal}
      />
      <AddAdyenTerminalModal
        isOpen={isAddTerminalOpen}
        onClose={() => setIsAddTerminalOpen(false)}
        onCreated={addTerminal}
        currentLocationId={locationId}
      />
      <AccessCodeModal
        isOpen={Boolean(createdAccessCode)}
        isLoading={false}
        isError={false}
        accessCode={createdAccessCode}
        handleOk={closeAccessCode}
      />
      {terminalToDelete != null && (
        <ConfirmationModal
          headerMessage='Delete terminal'
          bodyMessage='Are you sure you want to delete this terminal?'
          onCancelClick={() => setTerminalToDelete(null)}
          onConfirmClick={deleteTerminal}
        />
      )}
    </Grid>
  );
};

export default AdyenLocationDetails;